import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  IDealerLocatorContainer,
  Dealer,
  IDealerLocatorLabels,
  IDealerLocatorPageContent,
  Filter,
  IDealerLocator,
  FilterItem,
} from 'interfaces/dealerLocator';
import { theme } from 'theme/App.theme';
import { Grid, useMediaQuery } from '@mui/material';
import { useStyles } from './DealerLocatorContainer.styles';
import {
  FilterDropdown,
  getVisibleFilterItems,
} from 'components/DealerLocator/FilterDropdown/FilterDropdown';
import { TabList } from 'components/DealerLocator/TabList/TabList';
import { TabContent } from 'components/DealerLocator/TabContent/TabContent';
import { DealerCard } from 'components/DealerLocator/DealerCard/DealerCard';
import { DealerMap } from 'components/DealerLocator/DealerMap/DealerMap';
import { ZipInput } from 'components/DealerLocator/ZipInput/ZipInput';
import { DealerDetails } from 'components/DealerLocator/DealerDetails/DealerDetails';
import { Location } from 'components/Global/Location/Location';
import { useDispatch, useSelector } from 'react-redux';
import { preferredDealer, setSelectedDealer } from 'redux/slices/dealerSlice';
import {
  LinkButton,
  LinkButtonType,
} from 'components/Global/LinkButton/LinkButton';
import {
  useDealerLocatorDealers,
  useDealerLocatorPageContent,
} from 'hooks/useDealerLocatorData';
import { DealerLocatorSkeleton } from '../DealerLocatorSkeleton/DealerLocatorSkeleton';
import { Skeleton } from '@mui/material';
import TextRender from 'components/Global/TextRender/TextRender';
import { useLoadBingMapScript } from 'hooks/useLoadBingMapScript';
import { useQueryParameters } from 'hooks/useQueryString';
import { trackEvent } from 'helpers/Analytics';
import { updateUrlParameter } from 'helpers/Url';
import { useHistory } from 'react-router-dom';
import { selectZipcode } from 'redux/slices/zipcodeSlice';
import { appPalette } from 'theme/App.palette';

export function DealerLocatorContainer(
  props: IDealerLocatorContainer
): JSX.Element {
  const { status, data } = useDealerLocatorPageContent();
  const { isZipInputMobileHidden, isEmbedded } = props;

  switch (status) {
    case 'error':
      return <span className="error">{status}</span>;
    case 'success':
      return (
        <DealerLocatorSelection
          {...props}
          pageContent={data as IDealerLocatorPageContent}
        />
      );
    default:
      return (
        <DealerLocatorSkeleton
          isEmbedded={isEmbedded}
          isZipInputMobileHidden={isZipInputMobileHidden}
        />
      );
  }
}

function DealerLocatorSelection(props: IDealerLocator): JSX.Element {
  enum MapPositions {
    top = 'top',
    bottom = 'bottom',
  }
  const {
    isEmbedded,
    pageContent,
    customDealerShowMoreDelta,
    selectedDealer,
    isInModal,
  } = props;
  const queryParameters = useQueryParameters();
  const dispatch = useDispatch(); // redux dispatch
  const zipcode = useSelector(selectZipcode);
  const filtersQS = queryParameters.get('filters');
  const [coordinates, setCoordinates] = useState<
    { latitude: number; longitude: number } | undefined
  >(undefined);
  const currentPreferredDealerId = useSelector(preferredDealer);
  const dealersFetch = useDealerLocatorDealers(zipcode, coordinates);
  const dealerShowMoreDelta = useRef(
    customDealerShowMoreDelta || (pageContent.showMoreDelta as number) || 5
  );
  const currentTab = useRef(pageContent.labels.tabListView);
  const [tabValue, setTabValue] = useState(currentTab.current); // tabs for mobile
  const [detailedDealer, setDetailedDealer] = useState<Dealer | boolean>(false);
  const [dealerList, setDealerList] = useState(dealersFetch?.data); // dealer list that comes from the endpoint
  const [totalDisplayedCount, setTotalDisplayedCount] = useState(
    dealerShowMoreDelta.current
  ); //state to handle the total of dealers that are being displayed
  const [totalDealerCount, setTotalDealerCount] = useState(0); // state to handle the total of dealers that can be displayed.
  const [filters, setFilters] = useState<FilterItem[] | undefined>(); // filters that will be displayed in the dropdown
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const dealersContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapWidth, setMapWidth] = useState(
    mapContainerRef.current?.clientWidth
  );
  const [mapPosition, setMapPosition] = useState<MapPositions>(
    MapPositions.top
  );
  const [filtersQueryLoaded, setFiltersQueryLoaded] = useState(false);
  const matchesMediumBreakpoint = useMediaQuery(
    theme.breakpoints.up('viewportMedium'),
    { noSsr: true }
  );
  const matchesLargeBreakpoint = useMediaQuery(
    theme.breakpoints.up('viewportLarge'),
    { noSsr: true }
  );
  const navHeight = props.isInModal ? 0 : matchesLargeBreakpoint ? 70 : 50;
  const classes = useStyles({ navHeight });
  const isHondaThemed = theme.themeName === 'honda';
  const { honda_blue } = appPalette.colors;
  const scrollEventsAreOn =
    totalDisplayedCount >= dealerShowMoreDelta.current &&
    dealerList &&
    dealerList?.length > 0 &&
    mapPosition !== MapPositions.top;
  const bingMapScriptUrl = `https://www.bing.com/api/maps/mapcontrol?key=${pageContent.labels.bingMapsToken}`;
  const bingMapScriptStatus = useLoadBingMapScript(bingMapScriptUrl);
  const showMoreDealers = () => {
    setTotalDisplayedCount(totalDisplayedCount + dealerShowMoreDelta.current);
  };
  const history = useHistory();
  const getSelectedFilterCodes = () => {
    const selectedFilters = filters?.filter((filter) => filter.isSelected);
    const codes: string[][] = [];
    selectedFilters?.forEach((selectedFilter) => {
      const codeGroup: string[] = [];
      selectedFilter.codes?.forEach((codeElement) => {
        codeGroup.push(codeElement.code);
      });
      codes.push(codeGroup);
    });
    return codes;
  };

  const isFilterSelected = () => {
    return getSelectedFilterCodes().length > 0;
  };

  const handleDetailsClick = (dealer: Dealer) => {
    window.scrollTo({ top: 0 });
    trackEvent({
      label: `results:${dealer.name}`,
      category: {
        includePageName: true,
        text: 'results',
      },
      type: 'selector',
      dealer: {
        dealerId: dealer.dealerNumber,
        dealerZipcode: dealer.zipCode,
        dealerName: dealer.name,
        dealerAddress: `${dealer.address as string}, ${dealer.city}, ${
          dealer.state
        } ${dealer.zipCode}`,
      },
    });
    setDetailedDealer(dealer);
  };

  const onFilterChange = (e: ChangeEvent<{ value: unknown }>) => {
    if (e.target) {
      const filtersName = e.target.value as string[];
      const updatedFilters = [...(filters as FilterItem[])];
      updatedFilters.forEach((filter) => {
        if (filter.title && filtersName.indexOf(filter.title) > -1) {
          filter.isSelected = true;
        } else {
          filter.isSelected = false;
        }
      });
      setFilters([...updatedFilters]);
    }
  };

  const handleSetCoordinates = (newCoordinates: {
    latitude: number;
    longitude: number;
  }) => {
    if (newCoordinates && !isInModal) {
      matchesMediumBreakpoint && window.scrollTo({ top: 0 });
      setCoordinates(newCoordinates);
    }
  };
  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    currentTab.current = newValue;
    setTabValue(newValue);
  };

  const isDealerInFilter = (dealer: Dealer): boolean => {
    let isDealerInFilter = true;
    const filterCodeGroups = getSelectedFilterCodes();
    const dealerCodes = dealer.attributes.map((attribute) => attribute.code);
    if (filterCodeGroups.length === 0) {
      return true;
    }
    isDealerInFilter = filterCodeGroups.every((codeGroup) => {
      const intersection = dealerCodes?.filter((x) =>
        codeGroup.includes(x as string)
      );
      if (intersection?.length === 0) {
        return false;
      }
      return true;
    });
    return isDealerInFilter;
  };

  const getFilteredDealers = (listOfDealers: Dealer[]) => {
    return listOfDealers?.filter((dealer) => isDealerInFilter(dealer));
  };

  const getDealerIndex = (dealer: Dealer): number => {
    return (
      (getDealersToRender()
        ?.map((dealer: Dealer) => dealer.dealerNumber)
        .indexOf((detailedDealer as Dealer).dealerNumber) as number) + 1
    );
  };

  const orderDealers = (listOfDealers: Dealer[]) => {
    const preferredIndex = currentPreferredDealerId
      ? listOfDealers.findIndex(
          (dealer) => dealer.dealerNumber === currentPreferredDealerId
        )
      : -1;
    if (preferredIndex > -1) {
      listOfDealers.splice(0, 0, listOfDealers.splice(preferredIndex, 1)[0]); // placing the preferred dealer in the first position of the list
      listOfDealers.forEach((dealer) => (dealer.preferred = false));
      listOfDealers[0].preferred = true;
    } else {
      listOfDealers.forEach((dealer) => (dealer.preferred = false));
    }
    return listOfDealers;
  };

  const getDealersToRender = () => {
    let dealersToRender: Dealer[] = [];
    if (dealerList && dealerList?.length > 0) {
      dealersToRender = JSON.parse(JSON.stringify(dealerList as Dealer[])); // using deep copy of object instead of shallow copy to avoid overriding state array
      if (isFilterSelected()) {
        dealersToRender = getFilteredDealers(dealersToRender) as Dealer[];
      }
      dealersToRender = orderDealers(dealersToRender);
    }

    return dealersToRender.slice(0, totalDisplayedCount);
  };

  const renderDealers = () => {
    let dealersToRender = getDealersToRender();
    const dealerID = sessionStorage.getItem('dealerID');
    const dealerLockID = sessionStorage.getItem('dealerLockID');

    if (dealerLockID === 'true' && dealerID) {
      const dealer = dealersToRender?.find(
        (dealer) => dealer.dealerNumber === dealerID
      );

      if (dealer) {
        dispatch(setSelectedDealer(dealer));
      }
    }

    return (
      <>
        {dealersFetch.isLoading ? (
          <>
            {[...Array(3)].map((value: undefined, index: number) => (
              <Skeleton
                variant="rectangular"
                key={index}
                className={classes.cardSkeleton}
                height="14.3125rem"
              />
            ))}
            <Skeleton
              variant="rectangular"
              className={classes.showMoreSkeleton}
            />
          </>
        ) : (
          <>
            {dealerList &&
            dealerList.length > 0 &&
            dealersToRender.length > 0 ? (
              <Grid
                className={`${classes.dealerResultsContainer} st-styled-scroll`}
              >
                {dealersToRender.map((dealer: Dealer, index: number) => (
                  <DealerCard
                    labels={pageContent.labels as IDealerLocatorLabels}
                    CTAs={pageContent.ctas}
                    handleDetailsClick={handleDetailsClick}
                    useDealerCtas={!isEmbedded}
                    index={index + 1}
                    dealer={dealer}
                    key={dealer.dealerNumber}
                    isInModal={isInModal}
                  />
                ))}
              </Grid>
            ) : (
              <p className={`acr-paragraph-5 ${classes.noDealersText}`}>
                <TextRender text={pageContent.labels.noDealer} />
              </p>
            )}
            {totalDealerCount > totalDisplayedCount && (
              <LinkButton
                classNames={{
                  container: `${classes.showMoreButton} show-more-button`,
                  ...(theme.themeName === 'honda' && { font: 'atm-caption-7' }),
                }}
                onClick={showMoreDealers}
                trackEventData={{
                  label: `selector:more dealers`,
                  category: {
                    includePageName: true,
                    text: 'selector',
                  },
                  type: 'selector',
                }}
                variant={
                  isHondaThemed ? LinkButtonType.filled : LinkButtonType.red
                }
                color={isHondaThemed ? honda_blue : undefined}
              >
                <TextRender text={pageContent.labels.moreDealers} />
              </LinkButton>
            )}
          </>
        )}
      </>
    );
  };

  const renderFilterDropdown = () => {
    return (
      <Grid container direction="row" className={classes.searchContainer}>
        <ZipInput
          onChange={handleSetCoordinates}
          labels={pageContent.labels}
          isMobileHidden={props.isZipInputMobileHidden}
        />
        {!isHondaThemed &&
          props.showDealersDropdown !== false &&
          (filters ? (
            <FilterDropdown
              labels={pageContent.labels as IDealerLocatorLabels}
              items={filters}
              changeFunction={onFilterChange}
              disabled={
                (dealerList && dealerList?.length === 0) ||
                dealersFetch.isLoading
              }
              selectId={'filter'}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              className={classes.filterSkeleton}
            />
          ))}
      </Grid>
    );
  };

  const renderDealerMap = () => {
    let dealersToRender = getDealersToRender();
    if (bingMapScriptStatus === 'ready') {
      return (
        <DealerMap
          labels={pageContent.labels as IDealerLocatorLabels}
          dealers={dealersToRender}
          isEmbedded={isEmbedded}
          coordinates={coordinates}
          coordinatesSetter={handleSetCoordinates}
          infoboxActionOnClick={(dealer: Dealer) => {
            setDetailedDealer(dealer);
          }}
        />
      );
    }
  };

  const resetDealerCounter = (totalDealer: number) => {
    dealerShowMoreDelta.current =
      customDealerShowMoreDelta || (pageContent.showMoreDelta as number) || 5;
    const newTotalDisplayed =
      totalDealer < dealerShowMoreDelta.current
        ? totalDealer
        : dealerShowMoreDelta.current;
    setTotalDisplayedCount(newTotalDisplayed);
  };

  // function to handle the width of the map when the size of the screen changes. The width of the map
  // must be calculated dynamically due to the position fixed it has.
  const handleResize = function handleResize() {
    // Get the styles
    const styles = window.getComputedStyle(mapContainerRef.current as Element);

    // The size without padding and border
    const width =
      (mapContainerRef.current?.clientWidth as number) -
      parseFloat(styles.paddingLeft) -
      parseFloat(styles.paddingRight);
    setMapWidth(width);
  };

  const handleOwnersLink = () => {
    trackEvent({
      label: `selector:${pageContent.labels.bodyShopText}`,
      category: {
        includePageName: true,
        text: 'selector',
      },
      type: 'link',
    });
  };

  //set the total count of dealers that can be redered each time the filter change
  useEffect(() => {
    if (dealerList) {
      const filteredDealers = getFilteredDealers([...(dealerList as Dealer[])]);
      const totalDealers = filteredDealers.length;
      setTotalDealerCount(totalDealers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, dealerList]);

  useEffect(() => {
    const newDealerList =
      dealerList && dealerList?.length > 0
        ? orderDealers(JSON.parse(JSON.stringify(dealerList as Dealer[])))
        : []; // using deep copy of object instead of shallow copy to avoid overriding state array
    setDealerList(newDealerList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPreferredDealerId]);

  useEffect(() => {
    if (!detailedDealer) {
      setMapPosition(MapPositions.top);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedDealer]);

  useEffect(() => {
    if (mapContainerRef.current) {
      window.addEventListener('resize', handleResize, { passive: true });
      !mapWidth && handleResize();
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  });

  useEffect(() => {
    const totalDealer = dealersFetch?.data?.length || 0;
    if (dealersFetch?.data) {
      setTotalDealerCount(totalDealer);
      setFilters(
        getVisibleFilterItems(
          [...(pageContent.filters as Filter[])],
          dealersFetch?.data,
          props.preSelectedFilters?.split(',') || []
        )
      );
      setDealerList([...dealersFetch?.data]);
      resetDealerCounter(totalDealer);
      if (selectedDealer) {
        const found = dealersFetch.data.find((dealer) => {
          return dealer.dealerNumber === selectedDealer;
        });
        found
          ? dispatch(setSelectedDealer(found))
          : updateUrlParameter('dealer', 0, history);
      }
    } else if (
      !dealersFetch?.data &&
      !dealersFetch.isIdle &&
      !dealersFetch.isLoading
    ) {
      // setting the first tab to display the "no dealers message" in mobile.
      setTotalDealerCount(0);
      setDealerList([] as Dealer[]);
      currentTab.current = pageContent.labels.tabListView;
      setTabValue(currentTab.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealersFetch.data]);

  useEffect(() => {
    if (zipcode) {
      setCoordinates(undefined);
    }
  }, [zipcode]);

  useEffect(() => {
    if (filters && !filtersQueryLoaded) {
      const updatedFilters = [...(filters as FilterItem[])];
      const preSelectedFilters = props.preSelectedFilters || '';
      const filtersQSString = filtersQS || '';
      const filtersQSArray = filtersQSString.toLowerCase().split(',');
      const preSelectedFiltersArray = preSelectedFilters
        .toLowerCase()
        .split(',');

      updatedFilters?.forEach((filter) => {
        filter.codes?.forEach((codeElement) => {
          if (
            filtersQSArray.includes(codeElement.code.toLowerCase()) ||
            preSelectedFiltersArray.includes(codeElement.code.toLowerCase())
          ) {
            filter.isSelected = true;
          }
        });
      });
      setFilters([...updatedFilters]);
      setFiltersQueryLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <>
      {props.showLocationBar && (
        <Location
          onChange={handleSetCoordinates}
          labels={pageContent.labels as IDealerLocatorLabels}
        />
      )}
      {(props.title || props.description) && !detailedDealer && (
        <Grid container direction="row" className={classes.headerContainer}>
          <Grid container direction="row" className={classes.header}>
            {props.title && (
              <h1 className="acr-title-3">
                <TextRender text={pageContent.labels[props.title]} />
              </h1>
            )}
            {props.description && (
              <p className="acr-paragraph-4">
                <TextRender text={pageContent.labels[props.description]} />{' '}
                <LinkButton
                  onClick={() => {
                    handleOwnersLink();
                  }}
                  classNames={{ container: classes.body_shop_btn }}
                  target="_blank"
                  isExternal
                  to={pageContent.labels.bodyShopUrl}
                >
                  <TextRender text={pageContent.labels.bodyShopText} />
                </LinkButton>
              </p>
            )}
          </Grid>
        </Grid>
      )}
      <Grid
        container
        direction="row"
        className={`dealer-locator-container ${classes.root} ${
          !detailedDealer ? 'addMinHeight' : ''
        }`}
      >
        {detailedDealer ? (
          <DealerDetails
            CTAs={pageContent.ctas}
            labels={pageContent.labels as IDealerLocatorLabels}
            handleBackToDealerList={() => {
              setDetailedDealer(false);
            }}
            detailedDealer={detailedDealer as Dealer}
            dealerIndex={getDealerIndex(detailedDealer as Dealer)}
            isEmbedded={isEmbedded}
            isInModal={isInModal}
          />
        ) : (
          <>
            {matchesMediumBreakpoint ? (
              // desktop view
              <Grid container direction="row">
                <Grid
                  item
                  md={6}
                  className={classes.dealersContainer}
                  ref={dealersContainerRef}
                >
                  {renderFilterDropdown()}
                  {renderDealers()}
                </Grid>
                <Grid
                  item
                  md
                  className={classes.mapDesktopContainer}
                  ref={mapContainerRef}
                  id="container"
                >
                  <Grid
                    ref={mapRef}
                    container
                    className={`${classes.mapDesktop} ${
                      scrollEventsAreOn ? mapPosition : ''
                    }`}
                    style={{ maxWidth: mapWidth }}
                  >
                    {dealersFetch.isLoading ||
                    bingMapScriptStatus === 'loading' ? (
                      <Skeleton
                        animation="wave"
                        variant="rectangular"
                        className={classes.mapSkeleton}
                        height="100vh"
                      ></Skeleton>
                    ) : (
                      renderDealerMap()
                    )}
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              // mobile view
              <>
                {renderFilterDropdown()}
                <Grid
                  container
                  direction="row"
                  className={classes.tabContainer}
                >
                  <TabList
                    value={tabValue}
                    changeFunction={onTabChange}
                    labels={pageContent.labels as IDealerLocatorLabels}
                  ></TabList>
                  <TabContent
                    index={pageContent.labels.tabListView}
                    value={tabValue}
                  >
                    {renderDealers()}
                  </TabContent>
                  <TabContent
                    index={pageContent.labels.tabMapView}
                    value={tabValue}
                  >
                    <Grid container className={classes.mapContainer}>
                      {dealersFetch.isLoading ||
                      bingMapScriptStatus === 'loading' ? (
                        <Skeleton
                          animation="wave"
                          variant="rectangular"
                          className={classes.mapSkeleton}
                          height="100vh"
                        ></Skeleton>
                      ) : (
                        renderDealerMap()
                      )}
                    </Grid>
                  </TabContent>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </>
  );
}
